import { createSlice, createAsyncThunk } from '@reduxjs/toolkit'
import api from '../../api/api'

// ── Admin thunks ─────────────────────────────────────────────────────────
export const admin_get_referrals = createAsyncThunk(
    'referral/admin_get_referrals',
    async ({ page = 1, limit = 20, flagged = '', status = '' }, { rejectWithValue, fulfillWithValue }) => {
        try {
            const params = new URLSearchParams({ page, limit })
            if (flagged !== '') params.set('flagged', flagged)
            if (status) params.set('status', status)
            const { data } = await api.get(`/admin/referrals?${params}`, { withCredentials: true })
            return fulfillWithValue(data)
        } catch (error) {
            return rejectWithValue(error.response?.data)
        }
    }
)

export const admin_get_referral_settings = createAsyncThunk(
    'referral/admin_get_referral_settings',
    async (_, { rejectWithValue, fulfillWithValue }) => {
        try {
            const { data } = await api.get('/admin/referral-settings', { withCredentials: true })
            return fulfillWithValue(data)
        } catch (error) {
            return rejectWithValue(error.response?.data)
        }
    }
)

export const admin_update_referral_settings = createAsyncThunk(
    'referral/admin_update_referral_settings',
    async (settings, { rejectWithValue, fulfillWithValue }) => {
        try {
            const { data } = await api.put('/admin/referral-settings', settings, { withCredentials: true })
            return fulfillWithValue(data)
        } catch (error) {
            return rejectWithValue(error.response?.data)
        }
    }
)

export const admin_get_referral_users = createAsyncThunk(
    'referral/admin_get_referral_users',
    async ({ page = 1, limit = 20, userType = '' }, { rejectWithValue, fulfillWithValue }) => {
        try {
            const params = new URLSearchParams({ page, limit })
            if (userType) params.set('userType', userType)
            const { data } = await api.get(`/admin/referral-users?${params}`, { withCredentials: true })
            return fulfillWithValue(data)
        } catch (error) {
            return rejectWithValue(error.response?.data)
        }
    }
)

export const admin_adjust_wallet = createAsyncThunk(
    'referral/admin_adjust_wallet',
    async (payload, { rejectWithValue, fulfillWithValue }) => {
        try {
            const { data } = await api.put('/admin/referral-wallet', payload, { withCredentials: true })
            return fulfillWithValue(data)
        } catch (error) {
            return rejectWithValue(error.response?.data)
        }
    }
)

export const admin_flag_referral = createAsyncThunk(
    'referral/admin_flag_referral',
    async ({ referralId, flagged, flagReason }, { rejectWithValue, fulfillWithValue }) => {
        try {
            const { data } = await api.put(`/admin/referral/${referralId}/flag`, { flagged, flagReason }, { withCredentials: true })
            return fulfillWithValue(data)
        } catch (error) {
            return rejectWithValue(error.response?.data)
        }
    }
)

export const admin_backfill_codes = createAsyncThunk(
    'referral/admin_backfill_codes',
    async (_, { rejectWithValue, fulfillWithValue }) => {
        try {
            const { data } = await api.post('/admin/referral-codes/backfill', {}, { withCredentials: true })
            return fulfillWithValue(data)
        } catch (error) {
            return rejectWithValue(error.response?.data)
        }
    }
)

// ── Seller thunk ─────────────────────────────────────────────────────────
export const seller_get_referral = createAsyncThunk(
    'referral/seller_get_referral',
    async (_, { rejectWithValue, fulfillWithValue }) => {
        try {
            const { data } = await api.get('/seller/referral', { withCredentials: true })
            return fulfillWithValue(data)
        } catch (error) {
            return rejectWithValue(error.response?.data)
        }
    }
)

// ── Slice ─────────────────────────────────────────────────────────────────
const referralSlice = createSlice({
    name: 'referral',
    initialState: {
        referrals: [],
        totalReferrals: 0,
        referralUsers: [],
        totalReferralUsers: 0,
        settings: {},
        sellerReferral: null,
        successMessage: '',
        errorMessage: ''
    },
    reducers: {
        messageClear: (state) => {
            state.successMessage = ''
            state.errorMessage = ''
        }
    },
    extraReducers: (builder) => {
        builder
            .addCase(admin_get_referrals.fulfilled, (state, { payload }) => {
                state.referrals = payload.referrals
                state.totalReferrals = payload.total
            })
            .addCase(admin_get_referral_settings.fulfilled, (state, { payload }) => {
                state.settings = payload.settings
            })
            .addCase(admin_update_referral_settings.fulfilled, (state, { payload }) => {
                state.successMessage = payload.message
            })
            .addCase(admin_get_referral_users.fulfilled, (state, { payload }) => {
                state.referralUsers = payload.users
                state.totalReferralUsers = payload.total
            })
            .addCase(admin_adjust_wallet.fulfilled, (state, { payload }) => {
                state.successMessage = payload.message
            })
            .addCase(admin_flag_referral.fulfilled, (state, { payload }) => {
                state.successMessage = payload.message
                const idx = state.referrals.findIndex(r => r._id === payload.referral._id)
                if (idx !== -1) state.referrals[idx] = payload.referral
            })
            .addCase(admin_backfill_codes.fulfilled, (state, { payload }) => {
                state.successMessage = payload.message
            })
            .addCase(seller_get_referral.fulfilled, (state, { payload }) => {
                state.sellerReferral = payload
            })
            .addMatcher(
                (action) => action.type.startsWith('referral/') && action.type.endsWith('/rejected'),
                (state, { payload }) => {
                    state.errorMessage = payload?.error || 'Something went wrong'
                }
            )
    }
})

export const { messageClear } = referralSlice.actions
export default referralSlice.reducer
